import { TASK_STATE_TO_COLOR } from "dictionaries";
import qs from "qs";
import { SelecteFile } from "types/Models/documents";
import { Pagination } from "types/Models/pagination";
import {
  ClientProgress,
  ClientProgressCycle,
  Task,
  TaskCategoryType,
  TaskCompletedDocuments,
  TaskCompleteFileMetadata,
  TaskDocumentInstance,
  TaskFileUpdateResponse,
  TaskTags,
} from "types/Models/task";
import { emptyApi } from "./emptyApi";
import { BillingInvoice } from "types/Models/billing";
import { OPS_STAGES } from "types/contants/crm";
import { Tag } from "types/Models/FCA";
import { makeFormData } from "utils/makeFormData";
import { BooksGlobalStatus } from "types/Models/bookkeepingTracker";

export type ChannelTask = {
  uuid: string;
  name: string;
  deadline: null | string; // Change 'string' to a more specific date type if needed
  eligibility?: string;
  channel: {
    channel_url: string;
    name: string;
  };
  private_channel: {
    channel_url: string;
    name: string;
  };
  is_archived: boolean;
  current_state: {
    uuid: string;
    type: keyof typeof TASK_STATE_TO_COLOR;
    name: string;
  };
  state_details: {
    updated_at: string;
  };
  is_bundle: boolean;
  is_parent_task: boolean;
  assigned_to?: {
    uuid: string;
    name: string;
  };
  group_uuid?: string;
  base_task_key?: string;
  entity?: {
    uuid: string;
    name: string;
    country_name: string;
    code_alpha_2?: string;
  };
  client_progress: import("types/Models/task").ClientProgress | null;
  has_client_progress?: boolean;
  books_global_status?: BooksGlobalStatus | null;
  is_pricing_fixed?: boolean;
  latest_etd?: string | null;
  tracker_global_status?: string | null;
  total_task_states?: number;
  completed_task_states?: number;
  task_states?: import("types/Models/task").TaskDeadlineState[];
};

type Entity = {
  name: string;
  country_name: string;
  code_alpha_2: string;
  uuid: string;
};

type CompanyGroup = {
  ops_stage: OPS_STAGES;
  tags: Tag[];
  uuid: string;
  documents_group_id: string;
  assigned_agent?: {
    name: string;
    agent_id: string;
    profile_id: string;
  };
  books_2023_channel: {
    channel_url: string;
    name: string;
  };
  books_channels: {
    [key: string]: {
      channel_url: string;
      name: string;
    };
  };
  announcements_channel: {
    channel_url: string;
    name: string;
  };
  mailbox_channel: {
    channel_url: string;
    name: string;
  };
  secondary_channel: {
    channel_url: string;
    name: string;
  };
  private_channel: {
    channel_url: string;
    name: string;
  };
  name: string;
  unsorted_folder_id?: string;
};

export type TaskChannel = {
  tasks: ChannelTask[];
  entity?: Entity;
  company_group?: CompanyGroup;
};

export type EntityWithParentTaskDetails = {
  entity_name: string;
  entity_uuid: string;
  monthly_bookkeeping_task_status: boolean;
  country: string;
  country_code: string;
};

export type UpdateBulkTaskPayload = {
  task_ids: string[];
  service_team_id?: string;
  preparer_id?: string;
  estimated_delivery_time?: string;
  tags?: string[];
  deadline_date?: string;
};

export type FilingStage = {
  key: string;
  label: string;
  description?: string | null;
  uuid: string;
  is_current: boolean;
  is_completed: boolean;
  is_on_hold: boolean;
  date?: string | null;
};

export type FilingClientProgress = {
  current_key: string;
  current_label: string;
  stages: FilingStage[];
  is_on_hold: boolean;
  amendment_number?: number;
  previous_cycles?: ClientProgressCycle[];
};

export type FilingByStatus = {
  entity_id: string;
  entity_name: string;
  filing_name: string;
  filing_id: string;
  status: string;
  deadline: string;
  season: string;
  latest_etd?: string | null;
  client_progress?: FilingClientProgress | null;
};

export const taskApis = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getTask: build.query<Task, { taskId: string }>({
      query: ({ taskId }) => `api/inkle/tasks/${taskId}/`,
      providesTags: (result) =>
        result ? [{ type: "Tasks", id: result.uuid }] : [],
    }),
    getTaskTags: build.query<TaskTags[], void>({
      query: () => `api/inkle/tags/TASK_TAG/`,
    }),

    getTaskFromChannelId: build.query<TaskChannel, { channelId: string }>({
      query: ({ channelId }) => `/api/inkle/tasks/channel/${channelId}/task/`,
      providesTags: ["Tasks"],
    }),

    getClientProgress: build.query<ClientProgress | null, { taskId: string }>({
      query: ({ taskId }) => `/api/inkle/tasks/${taskId}/client-progress/`,
    }),

    setOnHold: build.mutation<void, { taskId: string; description?: string }>({
      query: ({ taskId, description }) => ({
        url: `/api/inkle/tasks/${taskId}/on-hold/`,
        method: "POST",
        body: { description },
      }),
      invalidatesTags: ["Tasks", "All_SERVICE_PROVIDERS"],
    }),

    removeOnHold: build.mutation<void, { taskId: string }>({
      query: ({ taskId }) => ({
        url: `/api/inkle/tasks/${taskId}/on-hold/`,
        method: "DELETE",
      }),
      invalidatesTags: ["Tasks", "All_SERVICE_PROVIDERS"],
    }),

    getFilingsByStatus: build.query<
      FilingByStatus[],
      { groupId: string; entityIds?: string[] }
    >({
      query: ({ groupId, entityIds }) => ({
        url: `/api/inkle/tasks/group/${groupId}/filings-by-status/`,
        // Omitted means the whole group, so an empty selection sends nothing.
        params: entityIds?.length ? { entity_ids: entityIds.join(",") } : {},
      }),
      providesTags: ["Tasks"],
    }),

    uploadToFormInstance: build.mutation<
      TaskFileUpdateResponse,
      {
        groupId: string;
        formInstanceId: string;
        tag: string;
        fieldKey: string;
        file?: File;
        file_id?: string;
      }
    >({
      query: ({ groupId, formInstanceId, tag, fieldKey, file, file_id }) => {
        const formData = new FormData();
        Object.entries({ field_key: fieldKey, file, tag, file_id }).forEach(
          ([key, value]) => value && formData.append(key, value)
        );

        return {
          url: `/api/inkle/tasks/group/${groupId}/form_instance/${formInstanceId}/file_upload/`,
          method: "post",
          body: formData,
        };
      },
    }),

    deleteFileFromTask: build.mutation<
      TaskFileUpdateResponse,
      {
        fileId: string;
        fieldKey: string;
        formInstanceId: string;
        groupId: string;
      }
    >({
      query: ({ fileId, fieldKey, formInstanceId, groupId }) => {
        const formData = new FormData();
        Object.entries({ field_key: fieldKey, file_id: fileId }).forEach(
          ([key, value]) => value && formData.append(key, value)
        );

        return {
          url: `/api/inkle/tasks/group/${groupId}/form_instance/${formInstanceId}/file_delete/`,
          method: "PUT",
          body: formData,
        };
      },
    }),
    uploadInkleDocToDocInstance: build.mutation<
      { doc_instance_uuid: string; doc_data: TaskDocumentInstance[] },
      {
        taskId: string;
        docInstanceId: string;
        tag: string;
        fieldKey: string;
        file: SelecteFile;
      }
    >({
      query: ({ taskId, docInstanceId, tag, fieldKey, file }) => {
        const formData = new FormData();
        Object.entries({
          field_key: fieldKey,
          file_id: file.uuid,
          tag,
        }).forEach(([key, value]) => value && formData.append(key, value));

        return {
          url: `/api/inkle/tasks/${taskId}/doc_instance/${docInstanceId}/file_upload/`,
          method: "post",
          body: formData,
        };
      },
      async onQueryStarted(
        { taskId, fieldKey, file },
        { dispatch, queryFulfilled }
      ) {
        try {
          dispatch(
            taskApis.util.updateQueryData("getTask", { taskId }, (draft) => {
              const fieldIndex =
                draft.doc_data.data[0].documents_needed.findIndex(
                  (f) => f.field_key === fieldKey
                );

              if (fieldIndex > -1) {
                draft.doc_data.data[0].documents_needed[
                  fieldIndex
                ].documents.push({
                  name: file.name,
                  uuid: file.uuid,
                  file_type: file.file_type,
                  is_previewable: file.is_previewable,
                  status: "UPLOADING",
                });
              }
            })
          );

          const { data: newDocumentInstance } = await queryFulfilled;

          dispatch(
            taskApis.util.updateQueryData("getTask", { taskId }, (draft) => {
              draft.doc_data.data = newDocumentInstance.doc_data;
            })
          );
        } catch {}
      },
    }),
    getTaskByBaseTemplateId: build.query<
      {
        task_uuid: string;
        task_title: string;
        task_deadline: string;
        current_state: { uuid: string; type: string; name: string };
      },
      { groupId: string; templateId: string; season: string }
    >({
      query: ({ groupId, templateId, season }) => {
        let queryUrl = qs.stringify(
          {
            season,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/tasks/group/${groupId}/base_template/${templateId}/task/${queryUrl}`,
        };
      },
      providesTags: () => {
        return [{ type: "TaskTemplate", id: "LIST" }];
      },
    }),
    uploadCompletedTaskFile: build.mutation<
      { data: TaskCompletedDocuments },
      {
        task_id: string;
        folder_id?: string;
        group_id: string;
        file?: File;
        file_id?: string;
        txn_merchant_id?: string;
        form_1099_document_type?: string;
      }
    >({
      query: ({
        task_id,
        folder_id,
        group_id,
        file,
        file_id,
        txn_merchant_id,
        form_1099_document_type,
      }) => {
        const formData = new FormData();
        Object.entries({
          folder_id: folder_id,
          file,
          txn_merchant_id,
          form_1099_document_type,
        }).forEach(([key, value]) => value && formData.append(key, value));
        return {
          url: `/api/inkle/tasks/group/${group_id}/task/${task_id}/file/`,
          method: "post",
          body: Boolean(file_id)
            ? { file_id: file_id, txn_merchant_id, form_1099_document_type }
            : formData,
        };
      },
      invalidatesTags: [{ type: "Tasks" }, { type: "Merchants" }],
    }),
    deleteCompletedTaskFile: build.mutation<
      { data: TaskCompletedDocuments[] },
      {
        task_document_map_id: string;
        task_id: string;
        group_id: string;
        // docUuid is used to show loader on deleting file
        docUuid?: string;
      }
    >({
      query: ({ task_document_map_id, task_id, group_id }) => {
        return {
          url: `/api/inkle/tasks/group/${group_id}/task/${task_id}/file/`,
          method: "PUT",
          body: {
            task_document_map_id,
          },
        };
      },
      invalidatesTags: [{ type: "Tasks" }],
    }),

    createOnboardingTask: build.mutation({
      query: ({
        payload,
      }: {
        payload: {
          task_template_id?: string;
          title: string;
          payment_category: number;
          payment_amount: string;
          company_entity_ids: string[];
          adhoc_scope_of_work: string;
        };
      }) => {
        return {
          url: `/api/inkle/tasks/company/task/`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: (result) => (result ? [{ type: "TP_ACCOUNTINFO" }] : []),
    }),

    getAllConsultantDashboardData: build.query<
      Pagination & { tasks: Task },
      {
        page_num: number;
        search_term?: string;
        task_list_type?: string;
        task_state_filter_uuids: string;
        task_template_filter_uuids?: string;
        task_tag_filter_uuids?: string;
        task_sort_by?: string;
        etd_type?: string;
      }
    >({
      query: ({
        page_num,
        search_term,
        task_list_type,
        task_state_filter_uuids,
        task_template_filter_uuids,
        task_tag_filter_uuids,
        task_sort_by,
        etd_type,
      }) => {
        let queryUrl = qs.stringify(
          {
            task_list_type,
            page_num,
            search_term,
            task_state_filter_uuids,
            task_template_filter_uuids,
            task_tag_filter_uuids,
            task_sort_by,
            etd_type,
          },
          { skipNulls: true, addQueryPrefix: true }
        );

        return {
          url: `/api/inkle/tasks/my_tasks/${queryUrl}`,
        };
      },
      providesTags: (result) =>
        result ? ["All_SERVICE_PROVIDERS", "Tasks", "NOTES"] : [],
    }),
    updateTask: build.mutation<
      any,
      {
        task_id: string;
        operation?: string;
        price?: string;
        description?: string;
        deadline?: string;
      }
    >({
      query: ({ task_id, operation, price, description, deadline }) => {
        // `/api/inkle/tasks/form_instance/${formId}/`;
        return {
          url: `/api/inkle/tasks/${task_id}/update/`,
          method: "PUT",
          body: {
            operation,
            price,
            description,
            deadline,
          },
        };
      },
      invalidatesTags: [{ type: "Tasks" }],
    }),
    updateTaskFormData: build.mutation<
      any,
      {
        formId: string;
        payload: any;
        taskId: string;
      }
    >({
      query: ({ formId, payload }) => {
        return {
          url: `/api/inkle/tasks/form_instance/${formId}/`,
          method: "PUT",
          body: payload,
        };
      },
      async onQueryStarted({ taskId, payload }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          taskApis.util.updateQueryData("getTask", { taskId }, (draft) => {
            draft.form_data.data.data = payload.form_data.data;
          })
        );

        try {
          await queryFulfilled;
        } catch (e) {
          patchResult.undo();
        }

        dispatch(taskApis.util.invalidateTags(["Tasks"]));
      },
    }),
    sendDocumentToReview: build.mutation<
      any,
      {
        payload: any;
        channel_id: string;
      }
    >({
      query: ({ channel_id, payload }) => {
        return {
          url: `/api/inkle/tasks/channel/${channel_id}/send_document_to_review/`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: [{ type: "Tasks" }],
    }),
    markDocumentAsReview: build.mutation<
      any,
      {
        document_ids: string;
        channel_id: string;
        message_id: string;
        group_id: string;
        task_id: string;
        link_ids?: string[];
      }
    >({
      query: ({
        document_ids,
        channel_id,
        message_id,
        group_id,
        task_id,
        link_ids,
      }) => {
        return {
          url: `/api/inkle/tasks/group/${group_id}/channel/${channel_id}/mark_documents_reviewed/`,
          method: "POST",
          body: { document_ids, message_id, task_id, link_ids },
        };
      },
      invalidatesTags: [{ type: "Tasks" }],
    }),
    getTaskInvoices: build.query<
      { amount: string; name: string; status: string }[],
      { groupId: string; taskId: string }
    >({
      query: ({ groupId, taskId }) =>
        `/api/inkle/tasks/group/${groupId}/task/${taskId}/task_invoices/`,
    }),
    markTaskAsReviewed: build.mutation<
      any,
      {
        taskId: string;
      }
    >({
      query: ({ taskId }) => {
        return {
          url: `/api/inkle/tasks/${taskId}/customer_action/`,
          method: "PUT",
          body: { mark_form_as_reviewed: true },
        };
      },
    }),
    updateState: build.mutation<
      Task,
      {
        taskId: string;
        newStateId: string;
      }
    >({
      query: ({ taskId, newStateId }) => {
        return {
          url: `/api/inkle/tasks/${taskId}/`,
          method: "PUT",
          body: { new_state_id: newStateId },
        };
      },
      invalidatesTags: [{ type: "Tasks" }, { type: "All_SERVICE_PROVIDERS" }],
    }),
    generateBooksInvoice: build.mutation<
      Task,
      {
        entityId: string;
        taskId: string;
        payload: {
          no_of_transactions?: number;
          cash_expense?: number;
          intra_group_transfers?: number;
          intra_company_transfers?: number;
          accounting_method?: string;
          from_date?: string | null;
          to_date?: string | null;
          monthly_billable_expenses_volume?: number;
        };
      }
    >({
      query: ({ entityId, taskId, payload }) => {
        return {
          url: `/api/inkle/tasks/entity/${entityId}/task/${taskId}/generate_books_invoice/`,
          method: "PUT",
          body: payload,
        };
      },
      invalidatesTags: [{ type: "Tasks" }],
    }),
    amendFiling: build.mutation<
      Task,
      {
        groupId: string;
        taskId: string;
        payload: any;
      }
    >({
      query: ({ groupId, taskId, payload }) => {
        return {
          url: `/api/inkle/tasks/group/${groupId}/task/${taskId}/amendment/`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: [{ type: "Tasks" }],
    }),

    getInProgressTaskCount: build.query<
      { in_progress_task_count: string },
      { groupId: string }
    >({
      query: ({ groupId }) =>
        `/api/inkle/tasks/in_progress_task_count/group/${groupId}/`,
      providesTags: (res) => (res ? ["Tasks"] : []),
    }),

    getAllBaseTaskTemplates: build.query<
      { uuid: string; title: string; task_category_type: TaskCategoryType }[],
      { searchString?: string | null }
    >({
      query: ({ searchString }) => {
        let queryUrl = qs.stringify(
          {
            search_string: searchString,
          },
          { skipNulls: true, addQueryPrefix: true }
        );

        return {
          url: `/api/inkle/tasks/base-task-templates/${queryUrl}`,
        };
      },
    }),

    getTaskSlaMetrics: build.query<
      {
        average_completed_time: {
          days: string;
          hours: string;
        };
        completed_filings_count: number;
        launched_filings_count: number;
      },
      { season: string }
    >({
      query: ({ season }) => {
        const queryUrl = qs.stringify({
          season,
        });
        return `api/inkle/tasks/sla_metrics/?${queryUrl}`;
      },
    }),

    generateTaskInvoice: build.mutation<
      BillingInvoice,
      { taskId: string; groupId: string }
    >({
      query: ({ groupId, taskId }) => {
        return {
          url: `/api/inkle/tasks/group/${groupId}/tasks/${taskId}/create_invoice/`,
          method: "POST",
        };
      },
    }),

    generateTaskInvoiceForFCA: build.mutation<
      BillingInvoice,
      { taskId: string; serviceTeamId: string }
    >({
      query: ({ serviceTeamId, taskId }) => {
        return {
          url: `api/inkle/tasks/service-team/${serviceTeamId}/tasks/${taskId}/create_invoice/`,
          method: "POST",
        };
      },
    }),
    getTaskEntityListByIds: build.query<
      Task[],
      { taskEntityIds: string; groupId: string }
    >({
      query: ({ taskEntityIds, groupId }) =>
        `api/inkle/tasks/group/${groupId}/task_list/?task_entity_ids=${taskEntityIds}`,
    }),
    updateTaskTaxAmout: build.mutation<
      BillingInvoice,
      { taskId: string; entityId: string; tax_amount_paid: string }
    >({
      query: ({ entityId, taskId, tax_amount_paid }) => {
        return {
          url: `api/inkle/tasks/entity/${entityId}/task/${taskId}/tax_amount/`,
          method: "PUT",
          body: { tax_amount_paid },
        };
      },
    }),
    confirmDFTData: build.mutation<
      BillingInvoice,
      {
        taskId: string;
        entityId: string;
        cc_emails: string[];
        to_emails: string[];
      }
    >({
      query: ({ entityId, taskId, cc_emails, to_emails }) => {
        return {
          url: `api/inkle/tasks/entity/${entityId}/task/${taskId}/data_confirmation/`,
          method: "PUT",
          body: { to_emails, cc_emails },
        };
      },
      invalidatesTags: [{ type: "Tasks" }],
    }),

    exportCSV: build.query<
      { name: string; ContentType: string; Content: string },
      {
        task_uuids?: string | null;
        task_type_key: string;
        [key: string]: unknown;
      }
    >({
      query: (params) => {
        const queryUrl = qs.stringify(params, {
          skipNulls: true,
          addQueryPrefix: true,
        });
        return {
          url: `api/inkle/tasks/provider/tasks/csv/${queryUrl}`,
        };
      },
    }),
    getEntityTasks: build.query<
      {
        uuid: string;
        title: string;
        state_type: string | null;
        state_name: string | null;
        deadline: string | null;
      }[],
      { base_task_template_uuids: string; season: string; entityId: string }
    >({
      query: ({ base_task_template_uuids, season, entityId }) => {
        let queryUrl = qs.stringify(
          {
            season,
            base_task_template_uuids,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `api/inkle/tasks/entity/${entityId}/status/${queryUrl}`,
        };
      },
    }),
    getEntityWithParentTask: build.query<
      EntityWithParentTaskDetails[],
      { groupId: string }
    >({
      query: ({ groupId }) => {
        return {
          url: `api/inkle/tasks/group/${groupId}/status/monthly_bookkeeping/`,
        };
      },
    }),
    updateBulkTask: build.mutation<void, { payload: UpdateBulkTaskPayload }>({
      query: ({ payload }) => {
        return {
          url: "api/inkle/tasks/v1/bulk-update/admin/",
          method: "PATCH",
          body: payload,
        };
      },
      invalidatesTags: ["TASK_1120_TRACKER", "All_SERVICE_PROVIDERS"],
    }),
    uploadCompletedTaskFileWithType: build.mutation<
      { data: TaskCompletedDocuments },
      {
        task_id: string;
        group_id: string;
        payload: {
          files_metadata: TaskCompleteFileMetadata[];
          [key: `file${number}`]: File;
        };
      }
    >({
      query: ({ task_id, group_id, payload }) => {
        return {
          url: `/api/inkle/tasks/group/${group_id}/task/${task_id}/files/`,
          method: "POST",
          body: makeFormData({
            ...payload,
            files_metadata: JSON.stringify(payload.files_metadata),
          }),
        };
      },
      invalidatesTags: [{ type: "Tasks" }, { type: "Merchants" }],
    }),

    submitCustomerFeedback: build.mutation<
      void,
      {
        feedbackId: string;
        groupId: string;
        payload: {
          rating: number;
          additional_feedback: string;
          good_feedback: string[];
          improve_feedback: string[];
        };
      }
    >({
      query: ({ feedbackId, groupId, payload }) => {
        return {
          url: `/api/inkle/messaging/group/${groupId}/customer-feedback/${feedbackId}/submit/`,
          method: "POST",
          body: payload,
        };
      },
    }),

    exportFilingForm: build.mutation<
      { download_url?: string },
      { taskId: string; export_to_email: boolean }
    >({
      query: ({ taskId, export_to_email }) => ({
        url: `/api/inkle/tasks/${taskId}/export-form/`,
        method: "POST",
        body: { export_to_email },
      }),
    }),
  }),

  overrideExisting: false,
});

export const {
  useGetInProgressTaskCountQuery,
  useGetTaskQuery,
  useUploadToFormInstanceMutation,
  useDeleteFileFromTaskMutation,
  useUploadInkleDocToDocInstanceMutation,
  useGetTaskTagsQuery,
  useGetTaskByBaseTemplateIdQuery,
  useUploadCompletedTaskFileMutation,
  useDeleteCompletedTaskFileMutation,
  useGetTaskFromChannelIdQuery,
  useLazyGetTaskFromChannelIdQuery,
  useGetFilingsByStatusQuery,
  useCreateOnboardingTaskMutation,
  useGetAllConsultantDashboardDataQuery,
  useUpdateTaskMutation,
  useSendDocumentToReviewMutation,
  useMarkDocumentAsReviewMutation,
  useGetTaskInvoicesQuery,
  useMarkTaskAsReviewedMutation,
  useUpdateStateMutation,
  useAmendFilingMutation,
  useGetAllBaseTaskTemplatesQuery,
  useUpdateTaskFormDataMutation,
  useGetTaskSlaMetricsQuery,
  useGenerateTaskInvoiceMutation,
  useGenerateTaskInvoiceForFCAMutation,
  useLazyGetTaskQuery,
  useGetTaskEntityListByIdsQuery,
  useUpdateTaskTaxAmoutMutation,
  useConfirmDFTDataMutation,
  useLazyExportCSVQuery,
  useGetEntityTasksQuery,
  useGetEntityWithParentTaskQuery,
  useGenerateBooksInvoiceMutation,
  useUpdateBulkTaskMutation,
  useUploadCompletedTaskFileWithTypeMutation,
  useSubmitCustomerFeedbackMutation,
  useExportFilingFormMutation,
  useGetClientProgressQuery,
  useLazyGetClientProgressQuery,
  useSetOnHoldMutation,
  useRemoveOnHoldMutation,
} = taskApis;
